import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '../context/AppContext'
import { api } from '../services/api'
import { 
  Camera, 
  FileText, 
  Download, 
  Calendar, 
  TrendingUp,
  Loader2,
  AlertCircle,
  Heart,
  Activity
} from 'lucide-react'

function HistoryPage() {
  const navigate = useNavigate()
  const { getScanHistory, downloadPDF, isLoading, scanHistory, user, apiCall } = useApp()
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('scans') // 'scans' or 'hra'
  const [hraHistory, setHraHistory] = useState([])
  const [loadingHRA, setLoadingHRA] = useState(false)

  useEffect(() => {
    if (activeTab === 'scans') {
      loadHistory()
    } else if (activeTab === 'hra') {
      loadHRAHistory()
    }
  }, [activeTab])

  const loadHistory = async () => {
    try {
      await getScanHistory()
    } catch (err) {
      setError(err.message || 'Failed to load scan history')
    }
  }

  const loadHRAHistory = async () => {
    try {
      setLoadingHRA(true)
      const response = await api.get('/hra/history', {
        params: {
          limit: 50
        }
      })
      // Filter out any null or invalid entries
      const validHistory = (response.data || []).filter(item => item && item.id)
      setHraHistory(validHistory)
    } catch (err) {
      console.error('Failed to load HRA history:', err)
      setHraHistory([])
    } finally {
      setLoadingHRA(false)
    }
  }

  const handleDownloadPDF = async (scanId) => {
    try {
      await downloadPDF(scanId)
    } catch (err) {
      console.error('Failed to download PDF:', err)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'processing':
        return 'bg-yellow-100 text-yellow-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getRiskColor = (riskCategory) => {
    switch (riskCategory) {
      case 'low': return 'bg-green-100 text-green-800'
      case 'moderate': return 'bg-yellow-100 text-yellow-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'very_high': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-blue" />
          <p className="text-gray-600">Loading scan history...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card max-w-md mx-auto text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              History
            </h1>
            <p className="text-gray-600">
              View your health scans and HRA assessments
            </p>
          </div>
          
          {activeTab === 'scans' && (
            <button
              onClick={() => navigate('/scan')}
              className="btn-primary flex items-center"
            >
              <Camera className="w-5 h-5 mr-2" />
              New Scan
            </button>
          )}
          {activeTab === 'hra' && (
            <button
              onClick={() => navigate('/hra')}
              className="btn-primary flex items-center"
            >
              <Heart className="w-5 h-5 mr-2" />
              New Assessment
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('scans')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'scans'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Activity className="w-4 h-4 mr-2" />
                Face Scan History
              </button>
              <button
                onClick={() => setActiveTab('hra')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'hra'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Heart className="w-4 h-4 mr-2" />
                HRA History
              </button>
            </nav>
          </div>
        </div>

        {/* Face Scan History Tab */}
        {activeTab === 'scans' && (
          <>
        {/* Stats Overview */}
        {scanHistory && scanHistory.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="card text-center">
              <div className="text-3xl font-bold text-primary-blue mb-2">
                {scanHistory.length}
              </div>
              <div className="text-gray-600">Total Scans</div>
            </div>
            
            <div className="card text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {scanHistory.filter(s => s.status === 'completed').length}
              </div>
              <div className="text-gray-600">Completed</div>
            </div>
            
            <div className="card text-center">
              <div className="text-3xl font-bold text-yellow-600 mb-2">
                {scanHistory.filter(s => s.status === 'processing').length}
              </div>
              <div className="text-gray-600">Processing</div>
            </div>
            
            <div className="card text-center">
              <div className="text-3xl font-bold text-gray-600 mb-2">
                {scanHistory.length > 0 ? 
                  (() => {
                    const scores = scanHistory.map(s => s.overall_health_score).filter(score => score !== null && score !== undefined)
                    return scores.length > 0 ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : 'N/A'
                  })()
                  : 'N/A'
                }
              </div>
              <div className="text-gray-600">Avg Health Score</div>
            </div>
          </div>
        )}

        {/* Scan History List */}
        {scanHistory && scanHistory.length > 0 ? (
          <div className="space-y-4">
            {scanHistory.map((scan) => (
              <div key={scan.id} className="card-hover">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary-blue text-white rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6" />
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {user?.name || 'User'}'s Health Scan #{scan.id.slice(-8)}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(scan.scan_date)}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(scan.status)}`}>
                          {scan.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {scan.overall_health_score !== null && scan.overall_health_score !== undefined ? (
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getScoreColor(scan.overall_health_score)}`}>
                          {scan.overall_health_score}
                        </div>
                        <div className="text-xs text-gray-500">Health Score</div>
                      </div>
                    ) : (
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-400">
                          N/A
                        </div>
                        <div className="text-xs text-gray-500">Health Score</div>
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      {scan.status === 'completed' && (
                        <>
                          <button
                            onClick={() => navigate(`/report/${scan.id}`)}
                            className="btn-secondary text-sm"
                          >
                            View Report
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(scan.id)}
                            className="btn-primary text-sm flex items-center"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </button>
                        </>
                      )}
                      
                      {scan.status === 'processing' && (
                        <div className="flex items-center text-yellow-600">
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </div>
                      )}
                      
                      {scan.status === 'failed' && (
                        <button
                          onClick={() => navigate('/scan')}
                          className="btn-secondary text-sm"
                        >
                          Retry Scan
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Scans Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start your first health scan to see your results here
            </p>
            <button
              onClick={() => navigate('/scan')}
              className="btn-primary"
            >
              Start Your First Scan
            </button>
          </div>
        )}

        {/* Trends Section */}
        {scanHistory && scanHistory.length > 1 && (
          <div className="card mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Health Trends
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Recent Improvements
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-green-800">Overall Health Score</span>
                    <span className="text-green-600 font-semibold">+5 points</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-green-800">Skin Health</span>
                    <span className="text-green-600 font-semibold">+3 points</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Areas to Focus On
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <span className="text-yellow-800">Sleep Quality</span>
                    <span className="text-yellow-600 font-semibold">Needs attention</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <span className="text-yellow-800">Stress Levels</span>
                    <span className="text-yellow-600 font-semibold">Monitor closely</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
          </>
        )}

        {/* HRA History Tab */}
        {activeTab === 'hra' && (
          <div>
            {loadingHRA ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-blue" />
                <p className="ml-3 text-gray-600">Loading HRA history...</p>
              </div>
            ) : hraHistory && hraHistory.length > 0 ? (
              <div className="space-y-4">
                {hraHistory.filter(assessment => assessment && assessment.id).map((assessment) => (
                  <div key={assessment.id} className="card-hover">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                          <Heart className="w-6 h-6" />
                        </div>
                        
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Health Risk Assessment
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {assessment.assessment_date ? formatDate(assessment.assessment_date) : 'N/A'}
                            </div>
                            {assessment.risk_category && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(assessment.risk_category)}`}>
                                {assessment.risk_category.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        {assessment.overall_risk_score !== undefined && (
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${getScoreColor(assessment.overall_risk_score)}`}>
                              {assessment.overall_risk_score}
                            </div>
                            <div className="text-xs text-gray-500">Risk Score</div>
                          </div>
                        )}
                        
                        <button
                          onClick={() => navigate('/hra')}
                          className="btn-secondary text-sm"
                        >
                          View Details
                        </button>
                      </div>
                    </div>

                    {/* Individual Risk Scores */}
                    {assessment.individual_risks && typeof assessment.individual_risks === 'object' && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(assessment.individual_risks)
                            .filter(([key, risk]) => risk && typeof risk === 'object' && risk.risk_score !== undefined)
                            .map(([key, risk]) => (
                            <div key={key} className="bg-gray-50 rounded-lg p-3">
                              <div className="text-xs text-gray-600 mb-1">
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </div>
                              <div className={`text-lg font-semibold ${getScoreColor(risk.risk_score)}`}>
                                {risk.risk_score}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {risk.risk_level || 'N/A'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="card text-center py-12">
                <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No HRA Assessments Yet
                </h3>
                <p className="text-gray-600 mb-6">
                  Complete your first Health Risk Assessment to see your results here
                </p>
                <button
                  onClick={() => navigate('/hra')}
                  className="btn-primary"
                >
                  Start Your First Assessment
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default HistoryPage
